import { ofType } from 'redux-observable'
import { merge, of } from 'rxjs'
import {
  catchError,
  concat,
  debounceTime,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { addToast } from 'containers/Toasts/actions'

import {
  CLEAR_DEEPFAKE_FILTERS,
  CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC,
  CREATE_DEEPFAKE_PROFILE_ASYNC,
  CREATE_DEEPFAKE_PROFILES_BULK_ASYNC,
  DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC,
  DELETE_DEEPFAKE_PROFILE_ASYNC,
  GET_DEEPFAKE_AD_GROUPS_ASYNC,
  GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC,
  GET_DEEPFAKE_ALL_USERS_ASYNC,
  GET_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC,
  GET_DEEPFAKE_MAILBOX_LIST_ASYNC,
  GET_DEEPFAKE_PROFILES_ASYNC,
  SET_DEEPFAKE_FILTERS,
  SET_DEEPFAKE_PROFILES_ORDERING,
  UPDATE_DEEPFAKE_PROFILE_ASYNC,
} from './constants'
import getDeepfakeMeetingsEpic from './meetingsEpics'

const MAILBOXES_PER_PAGE = 20
const ALL_USERS_PER_PAGE = 20
const ALL_USERS_SEARCH_DEBOUNCE_MS = 300
const AD_GROUPS_PER_PAGE = 100
const AD_GROUPS_SEARCH_DEBOUNCE_MS = 300
const NO_PROFILES_SELECTED_MESSAGE = 'No profiles selected.'

// Helper to build query params from filters
const buildDeepfakeKeyMembersParams = (filters = {}) => {
  const params = {}

  // Handle multiple status values - create array of status parameters
  if (filters.status && filters.status.length > 0) {
    if (Array.isArray(filters.status)) {
      params.status = filters.status // Keep as array for multiple status parameters
    } else {
      params.status = [filters.status] // Convert single status to array
    }
  }

  if (filters.search) params.search = filters.search
  if (filters.page) params.page = filters.page
  if (filters.page_size) params.page_size = filters.page_size
  if (filters.ordering) params.ordering = filters.ordering
  return params
}

const getDeepfakeProfilesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      CREATE_DEEPFAKE_PROFILE_ASYNC.SUCCESS,
      UPDATE_DEEPFAKE_PROFILE_ASYNC.SUCCESS,
      DELETE_DEEPFAKE_PROFILE_ASYNC.SUCCESS,
      GET_DEEPFAKE_PROFILES_ASYNC.INITIATE
    ),
    switchMap((action) => {
      const filters = state$.value.deepfake?.self?.filters || {}
      const params = buildDeepfakeKeyMembersParams(filters)

      // If triggered by SUCCESS action (not INITIATE), dispatch INITIATE first to set loader
      const isTriggeredBySuccess =
        action.type === CREATE_DEEPFAKE_PROFILE_ASYNC.SUCCESS ||
        action.type === UPDATE_DEEPFAKE_PROFILE_ASYNC.SUCCESS ||
        action.type === DELETE_DEEPFAKE_PROFILE_ASYNC.SUCCESS

      const apiCall$ = api.settings.getDeepfakeKeyMembers(params).pipe(
        mergeMap((response) => {
          const actions = [GET_DEEPFAKE_PROFILES_ASYNC.onSuccess(response)]

          // After create or delete, refetch non-deleted IDs and all users list
          if (
            action.type === CREATE_DEEPFAKE_PROFILE_ASYNC.SUCCESS ||
            action.type === DELETE_DEEPFAKE_PROFILE_ASYNC.SUCCESS
          ) {
            actions.push({
              type: GET_DEEPFAKE_ALL_USERS_ASYNC.INITIATE,
              payload: { page: 1, search: '' },
            })
          }

          return of(...actions)
        }),
        catchError((error) => {
          const actions = [epicsErrorHandler(GET_DEEPFAKE_PROFILES_ASYNC.onError, error)]

          const errorMessage = 'Failed to load profiles. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_PROFILES_ASYNC.CANCEL)))
      )

      // If triggered by SUCCESS, emit INITIATE first to set loader, then make API call
      if (isTriggeredBySuccess) {
        return of({ type: GET_DEEPFAKE_PROFILES_ASYNC.INITIATE }).pipe(concat(apiCall$))
      }

      return apiCall$
    })
  )

// New epic: refetch profiles on filter change
const filterDeepfakeProfilesEpic = (action$) =>
  action$.pipe(
    ofType(SET_DEEPFAKE_FILTERS, CLEAR_DEEPFAKE_FILTERS, SET_DEEPFAKE_PROFILES_ORDERING),
    map(() => ({ type: GET_DEEPFAKE_PROFILES_ASYNC.INITIATE }))
  )

const createDeepfakeProfileEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_DEEPFAKE_PROFILE_ASYNC.INITIATE),
    switchMap(() => {
      const employeeInfo = state$.value.deepfake.self.employeeInfo

      return api.settings
        .createDeepfakeKeyMember({
          profileId: employeeInfo.profileId,
          mediaFiles: employeeInfo.mediaFiles,
          employeeConsent: employeeInfo.hasConsent,
        })
        .pipe(
          mergeMap((response) =>
            of(
              CREATE_DEEPFAKE_PROFILE_ASYNC.onSuccess(response),
              addToast({
                message: 'Protected identity created successfully',
                type: 'success',
              })
            )
          ),
          catchError((error) => {
            const actions = [epicsErrorHandler(CREATE_DEEPFAKE_PROFILE_ASYNC.onError, error)]

            const errorMessage = 'Failed to create profile. Please try again.'

            actions.push(
              addToast({
                message: errorMessage,
                type: 'error',
              })
            )

            return of(...actions)
          }),
          takeUntil(action$.pipe(ofType(CREATE_DEEPFAKE_PROFILE_ASYNC.CANCEL)))
        )
    })
  )

const createDeepfakeProfilesBulkEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_DEEPFAKE_PROFILES_BULK_ASYNC.INITIATE),
    switchMap((action) => {
      const profileIds = action.payload?.profileIds || []

      if (!Array.isArray(profileIds) || profileIds.length === 0) {
        return of(
          CREATE_DEEPFAKE_PROFILES_BULK_ASYNC.onError({
            message: NO_PROFILES_SELECTED_MESSAGE,
          }),
          addToast({
            message: NO_PROFILES_SELECTED_MESSAGE,
            type: 'error',
          })
        )
      }

      return api.settings.createDeepfakeKeyMembersBulk({ profileIds }).pipe(
        mergeMap((response) => {
          const responseData = response?.response
          const successCount = Number(responseData?.successCount || 0)
          const skippedCount = Number(responseData?.skippedCount || 0)
          const errorCount = Number(responseData?.errorCount || 0)
          const createdTotal = successCount + skippedCount

          const actions = [CREATE_DEEPFAKE_PROFILES_BULK_ASYNC.onSuccess(responseData)]

          if (createdTotal > 0) {
            actions.push(
              addToast({
                message: `Created ${createdTotal} protected profile${
                  createdTotal === 1 ? '' : 's'
                }`,
                type: 'success',
              })
            )
          }

          if (errorCount > 0) {
            actions.push(
              addToast({
                message: `Failed to create ${errorCount} protected profile${
                  errorCount === 1 ? '' : 's'
                }`,
                type: 'error',
              })
            )
          }

          return of(...actions)
        }),
        catchError((error) => {
          const actions = [epicsErrorHandler(CREATE_DEEPFAKE_PROFILES_BULK_ASYNC.onError, error)]

          actions.push(
            addToast({
              message: 'Failed to create profiles. Please try again.',
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(CREATE_DEEPFAKE_PROFILES_BULK_ASYNC.CANCEL)))
      )
    })
  )

const updateDeepfakeProfileEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_DEEPFAKE_PROFILE_ASYNC.INITIATE),
    switchMap(() => {
      const employeeInfo = state$.value.deepfake.self.employeeInfo

      return api.settings
        .updateDeepfakeKeyMember(employeeInfo.id, {
          profileId: employeeInfo.profileId,
          mediaFiles: {
            imagesToCreate: employeeInfo.imagesToCreate || [],
            imagesToDelete: employeeInfo.imagesToDelete || [],
          },
        })
        .pipe(
          mergeMap((response) =>
            of(
              UPDATE_DEEPFAKE_PROFILE_ASYNC.onSuccess(response),
              addToast({
                message: 'Protected identity updated successfully',
                type: 'success',
              })
            )
          ),
          catchError((error) => {
            const actions = [epicsErrorHandler(UPDATE_DEEPFAKE_PROFILE_ASYNC.onError, error)]

            const errorMessage = 'Failed to update profile. Please try again.'

            actions.push(
              addToast({
                message: errorMessage,
                type: 'error',
              })
            )

            return of(...actions)
          }),
          takeUntil(action$.pipe(ofType(UPDATE_DEEPFAKE_PROFILE_ASYNC.CANCEL)))
        )
    })
  )

const deleteDeepfakeProfileEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_DEEPFAKE_PROFILE_ASYNC.INITIATE),
    switchMap((action) =>
      api.settings.deleteDeepfakeKeyMember(action.payload.id).pipe(
        mergeMap((response) =>
          of(
            DELETE_DEEPFAKE_PROFILE_ASYNC.onSuccess(response),
            addToast({
              message: 'Protected identity deleted successfully',
              type: 'success',
            })
          )
        ),
        catchError((error) => {
          const actions = [epicsErrorHandler(DELETE_DEEPFAKE_PROFILE_ASYNC.onError, error)]

          const errorMessage = 'Failed to delete profile. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(DELETE_DEEPFAKE_PROFILE_ASYNC.CANCEL)))
      )
    )
  )

const getDeepfakeMailboxListEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.INITIATE),
    switchMap((action) =>
      api.mailboxes
        .get(
          action.payload.page,
          '',
          undefined,
          undefined,
          '',
          '',
          '',
          action.payload.search,
          state$.value.partnerOptions.selectedCompany?.value,
          MAILBOXES_PER_PAGE
        )
        .pipe(
          map(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.onSuccess),
          catchError((error) => {
            const actions = [epicsErrorHandler(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.onError, error)]

            const errorMessage = 'Failed to load mailboxes. Please try again.'

            actions.push(
              addToast({
                message: errorMessage,
                type: 'error',
              })
            )

            return of(...actions)
          }),
          takeUntil(action$.pipe(ofType(GET_DEEPFAKE_MAILBOX_LIST_ASYNC.CANCEL)))
        )
    )
  )

const getDeepfakeAllUsersEpic = (action$, state$, { api, epicsErrorHandler }) => {
  const initiate$ = action$.pipe(ofType(GET_DEEPFAKE_ALL_USERS_ASYNC.INITIATE))

  const search$ = initiate$.pipe(
    filter((action) => {
      const page = action.payload?.page
      return !page || page <= 1
    })
  )

  const loadMore$ = initiate$.pipe(filter((action) => (action.payload?.page || 1) > 1))

  return merge(search$.pipe(debounceTime(ALL_USERS_SEARCH_DEBOUNCE_MS)), loadMore$).pipe(
    switchMap((action) => {
      const { page = 1, search = '' } = action.payload || {}

      return api.settings
        .getDeepfakeAvailableUsers({
          page,
          items_per_page: ALL_USERS_PER_PAGE,
          search: search || undefined,
        })
        .pipe(
          map((response) => {
            // Transform response format to match what reducer and component expect
            // Response has: { page, totalPages, totalCount, users: [{ firstName, lastName, ... }] }
            // Reducer expects: { items, count, perPage, page, search }
            // Component expects: { first_name, last_name, ... } (snake_case)
            const transformedUsers = (response.users || []).map((user) => ({
              ...user,
              first_name: user.firstName,
              last_name: user.lastName,
              phone_number: user.phoneNumber,
              risk_level: user.riskLevel,
              awareness_level: user.awarenessLevel,
              unprotected_reason: user.unprotectedReason,
            }))

            return GET_DEEPFAKE_ALL_USERS_ASYNC.onSuccess({
              items: transformedUsers,
              count: response.totalCount || 0,
              perPage: ALL_USERS_PER_PAGE,
              page: response.page || page,
              search,
            })
          }),
          catchError((error) => {
            const actions = [epicsErrorHandler(GET_DEEPFAKE_ALL_USERS_ASYNC.onError, error)]

            const errorMessage = 'Failed to load users. Please try again.'

            actions.push(
              addToast({
                message: errorMessage,
                type: 'error',
              })
            )

            return of(...actions)
          }),
          takeUntil(action$.pipe(ofType(GET_DEEPFAKE_ALL_USERS_ASYNC.CANCEL)))
        )
    })
  )
}

const getDeepfakeAdGroupsUserCountEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC.INITIATE),
    switchMap((action) => {
      const { groupIds } = action.payload || {}

      if (!groupIds || groupIds.length === 0) {
        return of(GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC.onSuccess({ results: [], groupIds: [] }))
      }

      return api.settings.getDeepfakeAdGroupsUserCount({ groupIds }).pipe(
        map((response) => {
          // Ensure we extract results correctly - handle both direct response and wrapped response
          let results = response?.response.results || response?.data?.results || response
          // Ensure results is always an array
          if (!Array.isArray(results)) {
            results = []
          }
          return GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC.onSuccess({
            results,
            groupIds,
          })
        }),
        catchError((error) =>
          of(
            GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC.onSuccess({
              results: groupIds.map((groupId) => ({
                groupId,
                userCount: null,
                error: 'Failed to fetch user count from AD',
              })),
              groupIds,
            })
          )
        ),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_AD_GROUPS_USER_COUNT_ASYNC.CANCEL)))
      )
    })
  )

const getDeepfakeAdGroupsEpic = (action$, state$, { api, epicsErrorHandler }) => {
  const initiate$ = action$.pipe(ofType(GET_DEEPFAKE_AD_GROUPS_ASYNC.INITIATE))

  const search$ = initiate$.pipe(
    filter((action) => {
      const page = action.payload?.page
      return !page || page <= 1
    })
  )

  const loadMore$ = initiate$.pipe(filter((action) => (action.payload?.page || 1) > 1))

  return merge(search$.pipe(debounceTime(AD_GROUPS_SEARCH_DEBOUNCE_MS)), loadMore$).pipe(
    switchMap((action) => {
      const { page = 1, search = '' } = action.payload || {}

      return api.settings
        .getDeepfakeAdGroups({
          page,
          page_size: AD_GROUPS_PER_PAGE,
          search: search.trim() || undefined,
        })
        .pipe(
          map((response) =>
            GET_DEEPFAKE_AD_GROUPS_ASYNC.onSuccess({
              ...response,
              page,
              search,
            })
          ),
          catchError((error) => {
            const actions = [epicsErrorHandler(GET_DEEPFAKE_AD_GROUPS_ASYNC.onError, error)]

            const errorMessage = 'Failed to load groups. Please try again.'

            actions.push(
              addToast({
                message: errorMessage,
                type: 'error',
              })
            )

            return of(...actions)
          }),
          takeUntil(action$.pipe(ofType(GET_DEEPFAKE_AD_GROUPS_ASYNC.CANCEL)))
        )
    })
  )
}

const getDeepfakeKeyPersonAdGroupsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.INITIATE),
    switchMap(() =>
      api.settings.getDeepfakeKeyPersonAdGroups().pipe(
        map((response) => GET_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.onSuccess(response)),
        catchError((error) => {
          const actions = [
            epicsErrorHandler(GET_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.onError, error),
          ]

          const errorMessage = 'Failed to load configured groups. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(GET_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.CANCEL)))
      )
    )
  )

const createDeepfakeKeyPersonAdGroupsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.INITIATE),
    switchMap((action) => {
      const { groupIds } = action.payload || {}

      if (!groupIds || groupIds.length === 0) {
        return of(
          CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.onError({
            error: 'No group IDs provided',
          })
        )
      }

      return api.settings.createDeepfakeKeyPersonAdGroups({ group_ids: groupIds }).pipe(
        map((response) => CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.onSuccess(response)),
        catchError((error) => {
          const actions = [
            epicsErrorHandler(CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.onError, error),
          ]

          const errorMessage = 'Failed to create key person AD groups. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(CREATE_DEEPFAKE_KEY_PERSON_AD_GROUPS_ASYNC.CANCEL)))
      )
    })
  )

const deleteDeepfakeKeyPersonAdGroupEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC.INITIATE),
    switchMap((action) => {
      const { id } = action.payload || {}

      if (!id) {
        return of(
          DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC.onError({
            error: 'No group ID provided',
          })
        )
      }

      return api.settings.deleteDeepfakeKeyPersonAdGroup(id).pipe(
        map(() =>
          // After successful delete, refresh the groups list
          DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC.onSuccess({ id })
        ),
        catchError((error) => {
          const actions = [
            epicsErrorHandler(DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC.onError, error),
          ]

          const errorMessage = 'Failed to remove group. Please try again.'

          actions.push(
            addToast({
              message: errorMessage,
              type: 'error',
            })
          )

          return of(...actions)
        }),
        takeUntil(action$.pipe(ofType(DELETE_DEEPFAKE_KEY_PERSON_AD_GROUP_ASYNC.CANCEL)))
      )
    })
  )

export default combineEpicsIsolated(
  getDeepfakeProfilesEpic,
  filterDeepfakeProfilesEpic,
  createDeepfakeProfileEpic,
  createDeepfakeProfilesBulkEpic,
  updateDeepfakeProfileEpic,
  deleteDeepfakeProfileEpic,
  getDeepfakeMailboxListEpic,
  getDeepfakeAllUsersEpic,
  getDeepfakeAdGroupsEpic,
  getDeepfakeAdGroupsUserCountEpic,
  getDeepfakeKeyPersonAdGroupsEpic,
  createDeepfakeKeyPersonAdGroupsEpic,
  deleteDeepfakeKeyPersonAdGroupEpic,
  getDeepfakeMeetingsEpic
)
